import { Button, message, Modal, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Sheet } from 'react-modal-sheet';

import { AppContext, RefreshStatus } from '../../../App';
import { ReactComponent as BackButton } from '../../../Assets/sib/back_button.svg';
import ConsentRevokeImg from '../../../Assets/yesbank/consentRevoke.png';
import { ReactComponent as FaqIcon } from '../../../Assets/sib/FaqIcon.svg';
import { ReactComponent as DownArrow } from '../../../Assets/yesbank/down-arrow.svg';
import { ReactComponent as UpArrow } from '../../../Assets/yesbank/up-arrow.svg';

import { ReactComponent as RefreshIcon } from '../../../Assets/sib/refresh.svg';
import { ReactComponent as BankShineIcon } from '../../../Assets/sib/bank-logo.svg';
import { ReactComponent as SpinLoader } from '../../../Assets/loader.svg';

import { refreshApi } from '../../../Services/PFMClient/DashboardService';
import { FlowType } from '../../../Utils/constants';
import { EVENTCODES, pushEventToParent } from '../../../Utils/eventUtils';
import { getAltJourney } from '../../../Utils/storageUtils';
import { getTrackingId } from '../../../Utils/storageUtils';
import { ROUTES } from '../../../Utils/routes';
import { altJourneyType, FIType } from '../../../Utils/types';
import {
  checkIsRefreshAvailable,
  formatToINR,
  getEarliestRefreshTimeforSIB,
  getMaximumLastRefreshedTime,
  getNextManualRefreshText,
  isRefreshDoneForFiTypes,
} from '../../../Utils/utils';

import AccountsCard from './AccountsCard';
import BankSummary from './BankSummary';
import CashFlow from './Cashflow';
import SIBFAQ from '../FAQ';

import SIBFooter from '../Common/Footer';
import styles from './index.module.css';

type Props = {
  defaultTab: any;
};

const SIBSpends: React.FC<Props> = (_props: Props) => {
  const {
    startAdditionalAccountJourney,
    forceRender,
    isAccountDetailsFetched,
    selectedNudge,
    setIsKeyInshigtsVisible,
    isKeyInshigtsVisible,
    setSelectedNudge,
    data,
    setIsCategoryAnalysisShown,
    isCategoryAnalysisShown,
    statusData,
    setStatusPolling,
    analyticsStatus,
    setAnalyticsStatus,
    setIsAccountDetailsFetched,
    monthlyPaymentsVisible,
    setMonthlyPaymentsVisible,
    setDataRefreshStatus,
    setIsAmountVisible,
    isAmountVisible,
    isSheetOpen,
    openSheet,
    closeSheet,
    termRecurringData,
    otherDepositBalance,
    setBackButtonHandler,
    currentRoute,
    setCurrentRoute,
    setPreviousRoute,
  }: any = useContext(AppContext);

  const [isDetailedAccounts, setIsDetailedAccounts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<any>();
  const [showFAQ, setShowFAQ] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const [isRefreshingManually, setIsRefreshingManually] = useState(false);
  const isBanner = useRef(true);
  const indexRef = useRef(-1);

  const handleBack = () => {
    if (showFAQ) {
      setShowFAQ(false);
      return;
    }
    if (selectedNudge !== undefined) {
      setSelectedNudge(undefined);
      if (monthlyPaymentsVisible) {
        setMonthlyPaymentsVisible(false);
      }
    } else if (isKeyInshigtsVisible) {
      setIsKeyInshigtsVisible(false);
    } else if (isDetailedAccounts) {
      window.scrollTo(0, 0);
      pushEventToParent({ eventCode: EVENTCODES.BACK_BUTTON_BANK });
      setIsDetailedAccounts(false);
    } else if (isCategoryAnalysisShown) {
      pushEventToParent({ eventCode: EVENTCODES.BACK_BUTTON_CATEGORIES });
      setIsCategoryAnalysisShown(false);
    } else {
      pushEventToParent({ eventCode: EVENTCODES.BACK_BUTTON_BANK_ACCOUNT });
      // setPreviousRoute(currentRoute);
      setCurrentRoute(ROUTES.DASHBOARD);
      // navigate('/dashboard');
    }
  };

  useEffect(() => {
    console.log('[BackButtonHandler] registering handler SIBSpends', {
      showFAQ,
      selectedNudge,
      isDetailedAccounts,
      isKeyInshigtsVisible,
      isCategoryAnalysisShown,
      monthlyPaymentsVisible,
    });
    setBackButtonHandler(() => handleBack);
    return () => {
      // setBackButtonHandler(() => () => {
      //   setPreviousRoute(currentRoute);
      //   setCurrentRoute(ROUTES.DASHBOARD);
      //   // navigate('/dashboard');
      // });
    };
  }, [
    showFAQ,
    selectedNudge,
    isDetailedAccounts,
    isKeyInshigtsVisible,
    isCategoryAnalysisShown,
    monthlyPaymentsVisible,
  ]);

  // Handle scroll state
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 18);
    window.addEventListener('scroll', handleScroll);
    window.scrollTo(0, 0);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // statusData.current.accountDetails can carry multiple historical entries
  // per linkRefNumber; only the latest one (by lastRawDataId) reflects the
  // account's current status. Everything below groups accounts by fipId, so
  // grouping over the raw array would render/count the same account twice.
  const latestAccountDetails = useMemo(() => {
    const latestByLinkRef = new Map<string, any>();
    (statusData.current.accountDetails || []).forEach((bank: any) => {
      const existing = latestByLinkRef.get(bank.linkRefNumber);
      if (
        !existing ||
        (bank.lastRawDataId ?? '') > (existing.lastRawDataId ?? '')
      ) {
        latestByLinkRef.set(bank.linkRefNumber, bank);
      }
    });
    return Array.from(latestByLinkRef.values());
  }, [statusData.current.accountDetails]);

  // Fetch recurring/term deposit accounts
  const fetchRecurringTermDepositAccounts = useMemo(() => {
    // Only ACTIVE accounts belong on the settled card. REVOKE_INITIATED
    // accounts are rendered on their own "revoking" card instead, so their
    // balance is never mixed into a card/total the way it used to be.
    return latestAccountDetails.filter(
      (acc: any) =>
        (acc.fiType === 'TERM_DEPOSIT' || acc.fiType === 'RECURRING_DEPOSIT') &&
        acc.status === 'ACTIVE' &&
        acc.lastRequestFIStatus === 'DELIVERED',
    );
  }, [latestAccountDetails]);

  // Fetch raw data by ID
  const fetchRawDataFromId = (id: string) => {
    const matchedData = termRecurringData.filter(
      (item: any) => item.dataRequestDetailsId === id,
    );
    if (matchedData.length === 0) return {};

    const rawData = matchedData[0].rawData;
    const holder = rawData.Profile;
    const summary = rawData.Summary;

    return {
      maturityAmount: summary.maturityAmount,
      maturityDate: summary.maturityDate,
      interestPayout: summary.interestPayout,
      interestRate: summary.interestRate,
      ifsc: summary.ifsc,
      holderName: holder?.Holders?.Holder?.[0]?.name || '',
      transaction: rawData?.Transactions?.Transaction?.map((txn: any) => ({
        ...txn,
        dateOfTransaction: txn.transactionDateTime,
      })),
    };
  };

  // Build accounts info
  const accountsInfo = useMemo(() => {
    const rawData = data.current.RAW_DATA || {};
    const consentPeriodsByAccountRef =
      data.current.ACCOUNT_DETAILS?.consentPeriodsByAccountRef || {};

    // Analytics-derived balance per account, used as a fallback when the raw
    // fetch balance is missing or comes back as 0 (e.g. a failed fetch).
    const closingBalanceByRef = new Map<string, number>(
      latestAccountDetails
        .filter((acc: any) => acc.linkRefNumber)
        .map((acc: any) => [acc.linkRefNumber, Number(acc.closingBalance) || 0]),
    );

    const allowedFiTypes = [
      'DEPOSIT',
      'TERM_DEPOSIT',
      'TERM-DEPOSIT',
      'RECURRING_DEPOSIT',
    ];
    const depositAccountRefs = new Set(
      latestAccountDetails
        ?.filter(
          // Only ACTIVE accounts feed the settled card; REVOKE_INITIATED
          // (revoking) and INACTIVE (revoked) render on their own cards.
          (acc: any) =>
            acc.status === 'ACTIVE' && allowedFiTypes.includes(acc.fiType),
        )
        .map((acc: any) => acc.linkRefNumber),
    );

    const accountProfileList =
      data.current.ACCOUNT_DETAILS?.accountProfileList || [];
    const depositAccounts = accountProfileList
      .filter((account: any) =>
        depositAccountRefs.has(account.account.linkedAccRef),
      )
      .map((account: any) => {
        const acc = account.account;
        const holder = account.holders;
        const summary = account.summary;
        const dailyAnalytics = account.dailyAnalytics;
        const consentParams = consentPeriodsByAccountRef[acc.linkedAccRef];

        return {
          id: acc.id,
          accNo: acc.number.slice(-6),
          balance:
            rawData?.[acc.linkedAccRef] ||
            closingBalanceByRef.get(acc.linkedAccRef) ||
            0,
          ifscCode: summary?.ifsc || '',
          fipName: acc.bank || '',
          accType: acc.acctype || '',
          fiType: 'DEPOSIT',
          consentStart: consentParams?.consentStart || '',
          consentExpiry: consentParams?.consentExpiry || '',
          holderName: holder?.Holder?.[0]?.name || '',
          holderAddress: holder?.Holder?.[0]?.address || '',
          holderEmail: holder?.Holder?.[0]?.email || '',
          dailyAnalytics,
          fipId: acc.fipId,
          date: acc.lastRawDataFetchedAt,
          consentReferenceId: acc.consentReferenceId,
          priority: acc.priority,
          linkedAccRef: acc.linkedAccRef,
          isNameMismatch: acc.isNameMismatch,
        };
      });

    const otherAccounts =
      fetchRecurringTermDepositAccounts?.map((account: any) => {
        const accountData = fetchRawDataFromId(account.lastRawDataId);
        const consentParams = consentPeriodsByAccountRef[account.linkRefNumber];

        return {
          id: account.lastRawDataId,
          accNo: account.maskedAccountNumber.slice(-6),
          balance:
            rawData[account.lastRawDataId] || account.closingBalance || 0,
          ifscCode: accountData.ifsc,
          fipName: account.fipName,
          accType: account.accountType,
          holderName: accountData.holderName,
          interestPayout: accountData.interestPayout,
          maturityDate: accountData.maturityDate,
          maturityAmount: accountData.maturityAmount,
          interestRate: accountData.interestRate,
          dailyAnalytics: {},
          fipId: account.fipId + '$success',
          date: account.lastRawDataFetchedAt,
          consentReferenceId: account.referenceId,
          priority: -1,
          linkedAccRef: account.linkRefNumber,
          fiType: account.fiType,
          isOtherFiType: true,
          transaction: accountData.transaction,
          consentStart: consentParams?.consentStart || '',
          consentExpiry: consentParams?.consentExpiry || '',
        };
      }) || [];

    const allAccounts = [...depositAccounts, ...otherAccounts].sort((a, b) => {
      if (a.priority === null) return 1;
      if (b.priority === null) return -1;
      return a.priority - b.priority;
    });

    return groupByKey(allAccounts, 'fipId');
  }, [
    isAccountDetailsFetched,
    data.current.ACCOUNT_DETAILS,
    termRecurringData,
    forceRender,
    latestAccountDetails,
  ]);

  // Get unique accounts
  const uniqueAccounts = useMemo(() => {
    const accountsSet = new Set();
    const accounts = latestAccountDetails;

    accounts
      .filter(
        (acc: any) =>
          acc.status === 'ACTIVE' || acc.status === 'REVOKE_INITIATED',
      )
      .forEach((acc: any) =>
        accountsSet.add(`${acc.fipId}_${acc.maskedAccountNumber}`),
      );

    return accounts.filter((acc: any) => {
      const key = `${acc.fipId}_${acc.maskedAccountNumber}`;
      if (
        acc.status === 'ACTIVE' ||
        acc.status === 'REVOKE_INITIATED' ||
        !accountsSet.has(key)
      ) {
        accountsSet.add(key);
        return true;
      }
      return false;
    });
  }, [latestAccountDetails, analyticsStatus, forceRender]);

  // Handle revoke success
  const handleRevokeSuccess = () => {
    setIsDetailedAccounts(false);
    openSheet();
  };

  // Get failure cases
  const failureCases = useMemo(() => {
    isBanner.current = true;
    indexRef.current = -1;

    const accounts = latestAccountDetails;

    const loadingAccounts = accounts
      .filter(
        (acc: any) =>
          acc.fiType === 'DEPOSIT' &&
          acc.lastAnalyzedAt === null &&
          acc.status === 'ACTIVE' &&
          !['DATA_NOT_RETURNED_IN_TIME', 'TIMEOUT', 'DENIED'].includes(
            acc.lastRequestFIStatus,
          ),
      )
      .map((acc: any) => ({
        ...acc,
        pending: true,
        fipId: acc.fipId + '$loading',
        balance: data.current?.RAW_DATA?.[acc.linkRefNumber] || 0,
      }));

    const loadingAccountsOther = accounts
      .filter(
        (acc: any) =>
          ['RECURRING_DEPOSIT', 'TERM_DEPOSIT'].includes(acc.fiType) &&
          acc.status === 'ACTIVE' &&
          ![
            'DATA_NOT_RETURNED_IN_TIME',
            'TIMEOUT',
            'DENIED',
            'DELIVERED',
          ].includes(acc.lastRequestFIStatus),
      )
      .map((acc: any) => ({
        ...acc,
        pending: true,
        isOtherFiType: true,
        fipId: acc.fipId + '$loadingOther',
        balance: data.current?.RAW_DATA?.[acc.lastRawDataId] || 0,
      }));

    const dataFailed = accounts
      .filter(
        (acc: any) =>
          acc.fiType === 'DEPOSIT' &&
          acc.lastAnalyzedAt === null &&
          ['DATA_NOT_RETURNED_IN_TIME', 'TIMEOUT', 'DENIED'].includes(
            acc.lastRequestFIStatus,
          ) &&
          acc.status === 'ACTIVE',
      )
      .map((acc: any) => ({
        ...acc,
        dataFailed: true,
        fipId: acc.fipId + '$failed',
        accNo: acc.maskedAccountNumber,
        linkedAccRef: acc.linkRefNumber,
        balance: acc.closingBalance ?? 0,
        date: acc.lastRawDataFetchedAt,
        accType: acc.accountType,
      }));

    const dataFailedOther = accounts
      .filter(
        (acc: any) =>
          ['RECURRING_DEPOSIT', 'TERM_DEPOSIT'].includes(acc.fiType) &&
          ['DATA_NOT_RETURNED_IN_TIME', 'TIMEOUT', 'DENIED'].includes(
            acc.lastRequestFIStatus,
          ) &&
          acc.status === 'ACTIVE',
      )
      .map((acc: any) => ({
        ...acc,
        dataFailed: true,
        isOtherFiType: true,
        fipId: acc.fipId + '$failedOther',
        accNo: acc.maskedAccountNumber,
        linkedAccRef: acc.linkRefNumber,
        balance: acc.closingBalance ?? 0,
        date: acc.lastRawDataFetchedAt,
        accType: acc.accountType,
      }));

    const revokedAccounts = (uniqueAccounts || [])
      .filter(
        (acc: any) => acc.fiType === 'DEPOSIT' && acc.status === 'INACTIVE',
      )
      .map((acc: any) => ({
        ...acc,
        revoke: true,
        fipId: acc.fipId + '$revoked',
      }));

    const revokedAccountsOther = (uniqueAccounts || [])
      .filter(
        (acc: any) =>
          ['RECURRING_DEPOSIT', 'TERM_DEPOSIT'].includes(acc.fiType) &&
          acc.status === 'INACTIVE',
      )
      .map((acc: any) => ({
        ...acc,
        revoke: true,
        isOtherFiType: true,
        fipId: acc.fipId + '$revokedOther',
      }));

    // REVOKE_INITIATED = a consent being revoked (not yet INACTIVE). These
    // render on their own "revoking" card (₹XXXX, not counted in the total),
    // so a bank with one revoking consent + one active consent no longer mixes
    // the revoking balance into the active card.
    const revokingAccounts = accounts
      .filter(
        (acc: any) =>
          acc.fiType === 'DEPOSIT' && acc.status === 'REVOKE_INITIATED',
      )
      .map((acc: any) => ({
        ...acc,
        revoking: true,
        fipId: acc.fipId + '$revoking',
        accNo: acc.maskedAccountNumber,
        linkedAccRef: acc.linkRefNumber,
        balance: data.current?.RAW_DATA?.[acc.linkRefNumber] || 0,
        date: acc.lastRawDataFetchedAt,
        accType: acc.accountType,
      }));

    const revokingAccountsOther = accounts
      .filter(
        (acc: any) =>
          ['RECURRING_DEPOSIT', 'TERM_DEPOSIT'].includes(acc.fiType) &&
          acc.status === 'REVOKE_INITIATED',
      )
      .map((acc: any) => ({
        ...acc,
        revoking: true,
        isOtherFiType: true,
        fipId: acc.fipId + '$revokingOther',
        accNo: acc.maskedAccountNumber,
        linkedAccRef: acc.linkRefNumber,
        balance: data.current?.RAW_DATA?.[acc.lastRawDataId] || 0,
        date: acc.lastRawDataFetchedAt,
        accType: acc.accountType,
      }));

    return {
      loadingAccounts: groupByKey(loadingAccounts, 'fipId'),
      loadingAccountsOther: groupByKey(loadingAccountsOther, 'fipId'),
      dataFailed: groupByKey(dataFailed, 'fipId'),
      dataFailedOther: groupByKey(dataFailedOther, 'fipId'),
      revokedAccounts: groupByKey(revokedAccounts, 'fipId'),
      revokedAccountsOther: groupByKey(revokedAccountsOther, 'fipId'),
      revokingAccounts: groupByKey(revokingAccounts, 'fipId'),
      revokingAccountsOther: groupByKey(revokingAccountsOther, 'fipId'),
    };
  }, [latestAccountDetails, uniqueAccounts, termRecurringData, forceRender]);

  // Clean accounts for detail view: merges FD accounts into their base bank key
  // but excludes failure-state entries (loading, failed, revoked).
  // Combine all accounts
  const allAccounts = useMemo(() => {
    // Insertion order drives render order. Live/in-progress cards (active,
    // analyzing, revoking) come first; revoked ("Previously tracked") stays
    // LAST so the "PREVIOUSLY TRACKED BANKS" section renders at the bottom and
    // a still-revoking card doesn't get stranded below it.
    const combined: Record<string, any[]> = {
      ...accountsInfo,
      ...failureCases.dataFailed,
      ...failureCases.dataFailedOther,
      ...failureCases.loadingAccounts,
      ...failureCases.loadingAccountsOther,
      ...failureCases.revokingAccounts,
      ...failureCases.revokingAccountsOther,
      ...failureCases.revokedAccounts,
      ...failureCases.revokedAccountsOther,
    };

    // Every state a bank's card key can carry, most visually important first.
    // Used only to pick the winning suffix WITHIN a state bucket (below).
    const suffixPriority = [
      '$loading',
      '$loadingOther',
      '$failed',
      '$failedOther',
      '$revoked',
      '$revokedOther',
      '$revoking',
      '$revokingOther',
      '$success',
      '',
    ];

    const splitKey = (key: string) => {
      const suffix = suffixPriority.find((s) => s !== '' && key.endsWith(s));
      return suffix
        ? { baseKey: key.slice(0, -suffix.length), suffix }
        : { baseKey: key, suffix: '' };
    };

    // Accounts of one bank that are in different states settle/change at
    // different times (an FD delivered while a savings account is still
    // analyzing, or one consent revoked while another stays active). Merging
    // them into one card makes the whole card show XXXX while the total already
    // counts the ready ones - a visible mismatch. So bucket each bank's keys by
    // STATE and render one card per state; only same-state keys merge together
    // (deposit + TD/RD settled into one card, $loading + $loadingOther into one
    // "analyzing" card, etc.). Each card is then consistent with the total: the
    // settled card's balance is counted; the analyzing, revoking and revoked
    // cards show XXXX and are not. (Failed accounts carry a 0 balance and stay
    // on the settled card so the user can still open them.)
    const stateBucket = (suffix: string) => {
      if (suffix === '$loading' || suffix === '$loadingOther') return '$loading';
      if (suffix === '$revoked' || suffix === '$revokedOther') return '$revoked';
      if (suffix === '$revoking' || suffix === '$revokingOther')
        return '$revoking';
      // '', '$success', '$failed', '$failedOther' all live on the settled/active
      // card: a failed account carries a 0 balance and is navigable, so it stays
      // with the bank's active accounts instead of a separate card.
      return '';
    };
    const keysByBaseFip: Record<string, string[]> = {};
    Object.keys(combined).forEach((key) => {
      const { baseKey, suffix } = splitKey(key);
      const groupKey = `${baseKey}${stateBucket(suffix)}`;
      (keysByBaseFip[groupKey] ||= []).push(key);
    });

    Object.values(keysByBaseFip).forEach((keys) => {
      if (keys.length <= 1) return;

      const winner = keys.reduce((best, key) => {
        const bestRank = suffixPriority.indexOf(splitKey(best).suffix);
        const rank = suffixPriority.indexOf(splitKey(key).suffix);
        return rank < bestRank ? key : best;
      });

      keys.forEach((key) => {
        if (key === winner) return;
        combined[winner] = [...combined[winner], ...combined[key]];
        delete combined[key];
      });

      // The same account can qualify for more than one state group at once
      // (e.g. it's already in accountsInfo but still pending analysis, so it
      // also lands in loadingAccounts) - keep only the winner group's copy.
      const seenRefs = new Set<string>();
      combined[winner] = combined[winner].filter((acc: any) => {
        // Active accounts (from accountsInfo) carry linkedAccRef; revoked/
        // loading/failed accounts (from raw accountDetails) carry
        // linkRefNumber. Without linkRefNumber here every raw-shaped account
        // collapses to `undefined` and all but the first get dropped.
        const ref = acc.linkedAccRef ?? acc.linkRefNumber ?? acc.id;
        if (seenRefs.has(ref)) return false;
        seenRefs.add(ref);
        return true;
      });
    });

    // SIB's own removed/disconnected accounts (Savings or FD) should
    // disappear entirely rather than show a "Connect" card - unlike accounts
    // held with other banks, which keep the Connect card so the user can
    // re-link them.
    Object.keys(combined).forEach((key) => {
      const accountList = combined[key];
      const isSIBOwnFip = key.split('$')[0] === 'SIB';
      const isRevoked = accountList.some((acc: any) => acc.revoke);
      if (isSIBOwnFip && isRevoked) {
        delete combined[key];
      }
    });

    return {
      account: combined,
      moreInfo: Object.keys(combined).length > 2,
    };
  }, [failureCases, accountsInfo]);

  // Helper function to group by key
  function groupByKey(array: any[], key: string) {
    return array.reduce((result: any, item: any) => {
      const groupKey = item[key];
      if (!result[groupKey]) result[groupKey] = [];
      result[groupKey].push(item);
      return result;
    }, {});
  }

  // Get linked accounts count
  const linkedAccountsCount = useMemo(() => {
    return latestAccountDetails.filter(
      (acc: any) =>
        ['DEPOSIT', 'TERM_DEPOSIT', 'RECURRING_DEPOSIT'].includes(acc.fiType) &&
        acc.status !== 'INACTIVE',
    ).length;
  }, [latestAccountDetails]);

  // Navigate back if no linked accounts
  useEffect(() => {
    if (linkedAccountsCount === 0) {
      setPreviousRoute(currentRoute);
      setCurrentRoute(ROUTES.DASHBOARD);
      // navigate('/dashboard');
    }
  }, [linkedAccountsCount]);

  // Check if has non-YesBank accounts
  const hasNonYesBankAccounts = useMemo(() => {
    const accounts = Object.values(accountsInfo || {}).flat();
    return accounts.some((account: any) => !account.fipId.includes('YES-BANK'));
  }, [accountsInfo]);

  // Get total balance
  const totalBalance = useMemo(() => {
    const accountDetails = latestAccountDetails;

    // status === 'ACTIVE' already excludes REVOKE_INITIATED and INACTIVE.
    // Only count accounts whose balance is actually displayed on the card.
    // A card hides its balance ("analyzing your data") while lastAnalyzedAt is
    // null, so those are excluded here to keep the total consistent with the
    // visible balances. Accounts being refreshed keep their previous
    // lastAnalyzedAt timestamp, so they still count.
    const depositAccounts = accountDetails.filter(
      (acc: any) =>
        acc.fiType === 'DEPOSIT' &&
        acc.status === 'ACTIVE' &&
        acc.lastAnalyzedAt !== null,
    );
    const depositBalance = depositAccounts.reduce(
      (sum: number, acc: any) =>
        sum +
        (data.current?.RAW_DATA?.[acc.linkRefNumber] || acc.closingBalance || 0),
      0,
    );
    const tdRdAccounts = accountDetails.filter(
      (acc: any) =>
        ['TERM_DEPOSIT', 'RECURRING_DEPOSIT'].includes(acc.fiType) &&
        acc.status === 'ACTIVE' &&
        acc.lastRequestFIStatus === 'DELIVERED',
    );
    const tdRdBalance = tdRdAccounts.reduce(
      (sum: number, acc: any) =>
        sum +
        (data.current?.RAW_DATA?.[acc.lastRawDataId] ||
          acc.closingBalance ||
          0),
      0,
    );
    return (depositBalance + tdRdBalance).toString();
  }, [
    otherDepositBalance.current,
    termRecurringData,
    analyticsStatus,
    isAccountDetailsFetched,
    latestAccountDetails,
    forceRender,
  ]);

  const manualRefreshFiTypes = [
    FIType.DEPOSIT,
    FIType.TERM_DEPOSIT,
    FIType.RECURRING_DEPOSIT,
  ];

  const earliestRefreshTime = useMemo(
    () =>
      getEarliestRefreshTimeforSIB(
        statusData.current?.accountDetails,
        manualRefreshFiTypes,
      ),
    [statusData.current?.accountDetails],
  );

  const maximumLastRefreshedTime = useMemo(
    () =>
      getMaximumLastRefreshedTime(
        statusData.current?.accountDetails,
        manualRefreshFiTypes,
      ),
    [statusData.current?.accountDetails],
  );

  const isManualRefreshAvailable = useMemo(
    () => checkIsRefreshAvailable(earliestRefreshTime),
    [earliestRefreshTime],
  );

  // A data refresh is actively fetching. lastRefreshStatus comes from the
  // status API, so this is correct across navigation AND across an SDK
  // close/reopen: if the user leaves mid-refresh and starts the journey again,
  // the fresh session reads lastRefreshStatus === 'PENDING' and still shows the
  // refreshing state / blocks another refresh. It flips to 'COMPLETED' once the
  // data lands (independent of analytics), so it won't linger like the old
  // dataRefreshStatus did. The local flags cover the instant between the click
  // and the next status poll.
  const isRefreshInProgress =
    isRefreshing ||
    isRefreshingManually ||
    statusData.current?.lastRefreshStatus === 'PENDING';

  const isAllRefreshDone = useMemo(
    () =>
      isRefreshDoneForFiTypes(
        statusData.current?.accountDetails,
        manualRefreshFiTypes,
      ),
    [statusData.current?.accountDetails, analyticsStatus],
  );

  // True only on first load before any account has FI data delivered.
  // Once any account gets DELIVERED, balance data is available to show.
  const isTotalBalanceLoading = useMemo(() => {
    // As soon as we have any settled balance to show, show the running total -
    // never a spinner. This keeps the total visible during a refresh or while
    // some accounts are still analyzing (their balances are already summed in),
    // instead of hiding everything behind a loader.
    if (Number(totalBalance) > 0) return false;
    const accounts = statusData.current.accountDetails || [];
    const activeDeposits = accounts.filter(
      (acc: any) =>
        ['DEPOSIT', 'TERM_DEPOSIT', 'RECURRING_DEPOSIT'].includes(acc.fiType) &&
        acc.status === 'ACTIVE',
    );
    if (activeDeposits.length === 0) return false;
    return !activeDeposits.some(
      (acc: any) => acc.lastRequestFIStatus === 'DELIVERED',
    );
  }, [totalBalance, statusData.current.accountDetails, analyticsStatus, forceRender]);

  // Handle refresh
  const handleRefresh = () => {
    if (isRefreshInProgress || !isManualRefreshAvailable) {
      return;
    }

    // Don't start a refresh while another operation is mid-flight on the same
    // accounts - it would race with the in-flight consent revoke / data fetch
    // and leave the totals inconsistent. Pause and tell the user instead.
    const hasRevokeInProgress = statusData.current.accountDetails.some(
      (acc: any) => acc.status === 'REVOKE_INITIATED',
    );
    if (hasRevokeInProgress) {
      message.info(
        <Typography.Text className={styles.infoAlertText}>
          Consent revocation in progress. Please try again in some time.
        </Typography.Text>,
      );
      return;
    }

    const hasAnalyzingInProgress =
      Object.keys(failureCases.loadingAccounts).length > 0 ||
      Object.keys(failureCases.loadingAccountsOther).length > 0;
    if (hasAnalyzingInProgress) {
      message.info(
        <Typography.Text className={styles.infoAlertText}>
          We're still fetching your latest data. Please try again in a moment.
        </Typography.Text>,
      );
      return;
    }

    setIsRefreshingManually(true);
    pushEventToParent({
      eventCode: EVENTCODES.USER_INITIATED_REFRESH_BUTTON_CLICKED,
    });
    setIsRefreshing(true);

    const trackingId = getTrackingId() ?? undefined;
    refreshApi(trackingId, manualRefreshFiTypes)
      .then(() => {
        setAnalyticsStatus('PENDING');
        setIsAccountDetailsFetched(false);
        setStatusPolling((prev: any) => prev + 1);
        setDataRefreshStatus(RefreshStatus.IN_PROGRESS);
        pushEventToParent({ eventCode: EVENTCODES.REFRESH_PROCESSING });
      })
      .catch(() => {
        setIsRefreshingManually(false);
        pushEventToParent({ eventCode: EVENTCODES.REFRESH_FAILED });
        message.error('Something went wrong. Try again after sometime');
      })
      .finally(() => setIsRefreshing(false));
  };

  useEffect(() => {
    if (isAllRefreshDone && isRefreshingManually) {
      setIsRefreshingManually(false);
    }
  }, [isAllRefreshDone]);

  // Handle add new account
  const handleAddAccount = () => {
    pushEventToParent({ eventCode: EVENTCODES.ADD_ACCOUNT_BANKS });
    setPreviousRoute(currentRoute || ROUTES.SIB_SPENDS);
    startAdditionalAccountJourney(FlowType.BANKS);
  };

  // Handle account card click
  const handleAccountClick = (account: string, index: number) => {
    const accountList = allAccounts.account[account];
    const hasRevoked = accountList.some((acc: any) => acc.revoke);
    const hasPending = accountList.some((acc: any) => acc.pending);

    const latestByLinkRef = new Map<string, any>(
      latestAccountDetails.map((bank: any) => [bank.linkRefNumber, bank]),
    );
    const hasRevokeInitiated = accountList.some(
      (acc: any) =>
        latestByLinkRef.get(acc.linkedAccRef)?.status === 'REVOKE_INITIATED',
    );
    const hasNameMismatch = accountList.some((acc: any) => acc.isNameMismatch);

    // A failed data fetch (single or multiple accounts) should still be
    // navigable so the user can open the bank's detail view - matching the
    // multi-account behaviour.
    if (
      !hasRevoked &&
      !hasPending &&
      !hasRevokeInitiated &&
      !hasNameMismatch
    ) {
      pushEventToParent({ eventCode: 'BANK_CLICKED_' + (index + 1) });
      setIsDetailedAccounts(true);
      setSelectedAccount(account);
    }
  };

  // Update status banner
  const updateStatusBanner = (index: number) => {
    isBanner.current = false;
    indexRef.current = index;
    return null;
  };

  // Render consent revoked content
  const renderRevokedContent = () => (
    <>
      <img
        src={ConsentRevokeImg}
        alt='Consent Revoked'
        className={`${styles.revokedImage} ${styles.revokedImage_yb}`}
      />
      <h4 className={styles.revokeText}>Consent Revoked!</h4>
      <button className={styles.revokedButton} onClick={closeSheet}>
        Okay
      </button>
    </>
  );

  return (
    <div className={styles.container}>
      {showFAQ && <SIBFAQ onBack={() => setShowFAQ(false)} />}
      <div style={{ display: showFAQ ? 'none' : 'block' }}>
        {/* Header */}
        <div className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <BackButton onClick={handleBack} className={styles.backButton} />
              <Typography.Text className={styles.headerTitle}>
                Account Summary
              </Typography.Text>
            </div>
            <FaqIcon
              className={styles.headerIcon}
              onClick={() => {
                if (isDetailedAccounts) {
                  setShowFAQ(true);
                } else {
                  setPreviousRoute(currentRoute);
                  setCurrentRoute(ROUTES.SIB_FAQ);
                }
              }}
            />
          </div>
        </div>

        {isDetailedAccounts ? (
          <BankSummary
            index={selectedAccount}
            accounts={allAccounts.account}
            onRevokeSuccess={handleRevokeSuccess}
            onBack={() => {
              window.scrollTo(0, 0);
              pushEventToParent({ eventCode: EVENTCODES.BACK_BUTTON_BANK });
              setIsDetailedAccounts(false);
            }}
          />
        ) : (
          <>
            {/* Balance Card */}
            <div className={styles.balanceCard}>
              {/* Inner box around total balance info */}
              <div className={styles.balanceInfoBox}>
                <Typography.Text className={styles.balanceLabel}>
                  TOTAL ACCOUNT BALANCE
                </Typography.Text>

                <div className={styles.actionButtonsRow}>
                  <div className={styles.linkedAccountsBtn}>
                    <BankShineIcon className={styles.bankIcon} />
                    <Typography.Text className={styles.linkedAccountsText}>
                      {linkedAccountsCount} Linked Account(s)
                    </Typography.Text>
                  </div>
                  <Button
                    type='text'
                    className={styles.addNewBtn}
                    onClick={handleAddAccount}
                  >
                    Add New
                  </Button>
                </div>

                <div className={styles.balanceContainer}>
                  <div className={styles.dashedDivider} />
                  {isTotalBalanceLoading ? (
                    <SpinLoader className={styles.balanceLoader} />
                  ) : (
                    <Typography.Text className={styles.balanceValue}>
                      {formatToINR(totalBalance, isAmountVisible)}
                    </Typography.Text>
                  )}
                  <div className={styles.dashedDivider} />
                </div>

                <div className={styles.refreshRow}>
                  {maximumLastRefreshedTime && (
                    <Typography.Text className={styles.lastRefreshed}>
                      Refreshed as on{' '}
                      {dayjs(maximumLastRefreshedTime).format("Do MMM' YYYY")}
                    </Typography.Text>
                  )}
                  {isRefreshInProgress ? (
                    <div className={styles.refreshingRow}>
                      <RefreshIcon
                        className={`${styles.refreshIcon} ${styles.refreshIconSpinning}`}
                      />
                      <span className={styles.refreshingText}>
                        Refreshing your data…
                      </span>
                    </div>
                  ) : isManualRefreshAvailable ? (
                    <Button
                      type='text'
                      className={styles.refreshBtn}
                      onClick={handleRefresh}
                    >
                      <RefreshIcon className={styles.refreshIcon} />
                      <span>Refresh Now</span>
                    </Button>
                  ) : null}
                </div>
                {!isRefreshInProgress && !isManualRefreshAvailable && (
                  <Typography.Text className={styles.refreshStatusText}>
                    {getNextManualRefreshText(earliestRefreshTime)}
                  </Typography.Text>
                )}
              </div>

              {/* Divider between balance and accounts */}
              <div className={styles.balanceDivider} />

              {/* Accounts List inside Balance Card */}
              <div className={styles.accountsList}>
                {(showAllAccounts
                  ? Object.keys(allAccounts.account)
                  : Object.keys(allAccounts.account).slice(0, 2)
                ).map((account, index) => {
                  const accountList = allAccounts.account[account];
                  const hasRevoked = accountList.some((acc: any) => acc.revoke);
                  const showBanner =
                    hasRevoked &&
                    (isBanner.current || index === indexRef.current);

                  return (
                    <React.Fragment key={account}>
                      {showBanner && (
                        <>
                          <h4 className={styles.sectionHeading}>
                            PREVIOUSLY TRACKED BANKS
                          </h4>
                          {updateStatusBanner(index)}
                        </>
                      )}
                      <div className={styles.accountCardWrapper}>
                        <AccountsCard
                          bankDetails={accountList}
                          fipId={account.split('$')[0]}
                          isConsentRevoked={accountList.some(
                            (acc: any) => acc.revoke,
                          )}
                          isFetchingData={accountList.some(
                            (acc: any) => acc.pending,
                          )}
                          isFailure={
                            accountList.some((acc: any) => acc.dataFailed) &&
                            !accountList.some(
                              (acc: any) =>
                                !acc.dataFailed && !acc.pending && !acc.revoke,
                            )
                          }
                          isOtherFiType={accountList.some(
                            (acc: any) => acc.isOtherFiType,
                          )}
                          isNameMismatch={accountList.some(
                            (acc: any) => acc.isNameMismatch,
                          )}
                          onCardClick={() => handleAccountClick(account, index)}
                        />
                      </div>
                    </React.Fragment>
                  );
                })}

                {allAccounts.moreInfo && (
                  <Typography.Link
                    onClick={() => setShowAllAccounts(!showAllAccounts)}
                    className={styles.viewMoreLink}
                  >
                    {showAllAccounts ? (
                      <>
                        <UpArrow color='#ED1F47' /> Hide Accounts
                      </>
                    ) : (
                      <>
                        <DownArrow color='#ED1F47' /> View More
                      </>
                    )}
                  </Typography.Link>
                )}
              </div>
            </div>

            {/* CashFlow Section */}
            <CashFlow onOpenFAQ={() => setShowFAQ(true)} />

            {/* Help Section */}
            <SIBFooter />
          </>
        )}

        {/* Consent Revoked Sheet/Modal */}
        {getAltJourney() === altJourneyType.YESONLINE &&
        window.innerWidth > 428 ? (
          <Modal
            rootClassName='modalRoot'
            closeIcon={null}
            className='revokeConsentSheet ybModal'
            footer={null}
            open={isSheetOpen}
          >
            {renderRevokedContent()}
          </Modal>
        ) : (
          <Sheet
            isOpen={isSheetOpen}
            onClose={closeSheet}
            detent='content-height'
            disableScrollLocking={true}
          >
            <Sheet.Container className={styles.sheetContainer}>
              <Sheet.Header />
              <Sheet.Content>{renderRevokedContent()}</Sheet.Content>
            </Sheet.Container>
            <Sheet.Backdrop onTap={closeSheet} />
          </Sheet>
        )}
      </div>
    </div>
  );
};

export default SIBSpends;
